import React from 'react'

/**
 * Tone semantics for {@link MetricTile} values. Mirrors {@link PillTone}
 * but maps to text color rather than background, since metric tiles use
 * the value's color to communicate polarity (e.g. green for "good",
 * amber for "watch this").
 */
export type MetricTone = 'neutral' | 'ok' | 'warn' | 'bad' | 'accent'

export type MetricTileProps = {
  label: string
  value: React.ReactNode
  /** Optional secondary line under the big value (e.g. "5 of 12"). */
  caption?: React.ReactNode
  tone?: MetricTone
  /** Optional title tooltip describing the metric. */
  title?: string
  /** Render in a denser "kpi" variant suited for dashboard top rows. */
  variant?: 'panel' | 'kpi'
  /** Visually emphasise this tile (heavier border / brighter background). */
  emphasis?: boolean
  /** Optional click handler. When provided, the tile renders as a button
   *  for keyboard accessibility. */
  onClick?: () => void
}

const TONE_TO_COLOR: Record<MetricTone, string> = {
  neutral: 'var(--text, #f3f4f6)',
  ok: 'var(--ok, #16a34a)',
  warn: 'var(--warn, #f59e0b)',
  bad: 'var(--bad, #dc2626)',
  accent: 'var(--accent, #6366f1)',
}

export default function MetricTile({
  label,
  value,
  caption,
  tone = 'accent',
  title,
  variant = 'panel',
  emphasis = false,
  onClick,
}: MetricTileProps) {
  const valueColor = TONE_TO_COLOR[tone]
  const baseStyle: React.CSSProperties =
    variant === 'kpi'
      ? {
          padding: 14,
          borderRadius: 14,
          border: '1px solid var(--border)',
          background: emphasis ? 'rgba(99,102,241,0.10)' : 'var(--card)',
        }
      : {
          padding: 12,
          borderRadius: 14,
          background: emphasis ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.07)',
          border: '1px solid var(--border)',
        }
  const interactive = !!onClick
  const Tag = (interactive ? 'button' : 'div') as keyof JSX.IntrinsicElements
  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      title={title}
      style={{
        ...baseStyle,
        textAlign: 'left',
        cursor: interactive ? 'pointer' : 'default',
        font: 'inherit',
        color: 'inherit',
        width: '100%',
      } as React.CSSProperties}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: 'var(--muted)',
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div style={{ marginTop: 4, fontSize: variant === 'kpi' ? 28 : 24, fontWeight: 800, color: valueColor }}>
        {value}
      </div>
      {caption ? (
        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--muted)' }}>{caption}</div>
      ) : null}
    </Tag>
  )
}
